import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AudioWaveformCard from '../components/AudioWaveformCard';
import Button from '../components/Button';
import { watermarkedDemoMarkers } from '../data/demoAudioMetadata';
import './HomePage.css';

const aowlIntroUrl = 'https://www.aowl.app/media/063b7f5d-2941-4245-8431-2f2e5c475ff7/';
const chromeExtensionUrl = 'https://chromewebstore.google.com/detail/aowl-active-tab-watermark/beagmjnmnkkknmbbphhnakpndilgjfnf?hl=en-US&utm_source=ext_sidebar';

class HomePage extends Component {
  render() {
    return (
      <div className="home-page">
        <section className="hero-section">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">Audio identity for the GenAI era</p>
              <h1 className="hero-title">Protect every voice with invisible proof.</h1>
              <p className="hero-subtitle">
                AOWL embeds resilient, inaudible identity into voice and audio so creators,
                platforms, and listeners can verify origin without changing the listening experience.
              </p>
              <div className="hero-actions">
                <Button
                  variant="primary"
                  href="https://aowl.app"
                  external={true}
                >
                  <img
                    src="/images/aowl-logo.003.png"
                    alt=""
                    className="button-logo"
                  />
                  Open AOWL
                </Button>
                <Link to="/technology" className="btn btn-secondary">Explore technology</Link>
                <Button
                  variant="secondary"
                  href={chromeExtensionUrl}
                  external={true}
                >
                  Try real-time detection
                </Button>
              </div>
            </div>

            <div className="hero-panel" aria-label="AOWL watermark preview">
              <div className="panel-header">
                <div>
                  <span className="panel-kicker">Watermark signal</span>
                  <h2>Voice asset verified</h2>
                </div>
                <span className="status-pill">Live</span>
              </div>
              <div className="waveform-preview">
                <span className="wave-bar tall" />
                <span className="wave-bar" />
                <span className="wave-bar medium" />
                <span className="wave-bar tall" />
                <span className="wave-bar short" />
                <span className="wave-bar medium" />
                <span className="wave-bar tall" />
                <span className="wave-bar" />
                <span className="wave-bar medium" />
                <span className="wave-bar short" />
                <span className="wave-bar tall" />
                <span className="wave-bar medium" />
                <span className="wave-bar short" />
                <span className="wave-bar tall" />
                <span className="wave-bar medium" />
                <span className="wave-bar" />
                <span className="wave-bar tall" />
                <span className="wave-bar short" />
                <span className="wave-bar medium" />
                <span className="wave-bar tall" />
                <span className="wave-bar" />
                <span className="wave-bar medium" />
                <span className="wave-bar short" />
                <span className="wave-bar tall" />
              </div>
              <div className="signal-grid">
                <div>
                  <span>Key density</span>
                  <strong>6T+ yrs</strong>
                  <p>Massive key space helps every protected asset carry its own durable identity.</p>
                </div>
                <div>
                  <span>Detection</span>
                  <strong>ms scale</strong>
                  <p>Fast checks support rapid review when suspicious audio appears.</p>
                </div>
                <div>
                  <span>Audio quality</span>
                  <strong>Transparent</strong>
                  <p>Protection stays inaudible, so listeners hear the original creative experience.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="aowl-intro-section">
          <div className="container aowl-intro">
            <div className="aowl-intro-copy">
              <p className="eyebrow">AOWL in public</p>
              <h2>Make every official clip provable before a fake one owns the story.</h2>
              <p>
                Built for PR teams, talent managers, and creators who cannot wait for the internet
                to calm down. AOWL turns protected media into a public trust surface: visible
                provenance for viewers, inaudible watermarking underneath, and a clean path back to
                proof.
              </p>
              <Button
                variant="primary"
                href={aowlIntroUrl}
                external={true}
                className="aowl-intro-button"
              >
                <img
                  src="/images/aowl-logo.003.png"
                  alt=""
                  className="button-logo"
                />
                Watch on AOWL
              </Button>
            </div>
            <div
              className="aowl-media-card"
              aria-label="AOWL public media intro video"
            >
              <div className="aowl-media-topline">
                <span>Public AOWL media</span>
                <span>AOWL preview</span>
              </div>
              <a
                className="aowl-media-stage aowl-screenshot-preview"
                href={aowlIntroUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch the AOWL public media intro"
              >
                <img
                  src="/images/aowl-preview.jpg"
                  alt="AOWL public media preview"
                />
                <div className="aowl-recording-overlay">
                  <span className="aowl-recording-brand">
                    <img src="/images/aowl-logo.003.png" alt="" />
                    AOWL preview
                  </span>
                  <span className="aowl-recording-play" aria-hidden="true">
                    Watch on AOWL
                  </span>
                </div>
              </a>
              <div className="aowl-media-footer">
                <div>
                  <strong>Owlboy world tour</strong>
                  <span>Watermarked video intro</span>
                </div>
                <a href={aowlIntroUrl} target="_blank" rel="noopener noreferrer">
                  View on AOWL
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="audience-section">
          <div className="container">
            <div className="section-intro">
              <p className="eyebrow">Reputation defense</p>
              <h2>Prove what is real before a fake voice becomes the story.</h2>
              <p>
                AOWL is designed for reputation-critical voice assets: licensed creator audio,
                campaign recordings, platform media, and high-value voices that need provenance
                without adding friction to distribution.
              </p>
            </div>
            <div className="audience-grid">
              <div className="audience-card">
                <span className="audience-label">PR teams</span>
                <h3>Respond with evidence</h3>
                <p>Check suspicious clips against protected audio before a rumor turns into a crisis.</p>
              </div>
              <div className="audience-card">
                <span className="audience-label">Talent teams</span>
                <h3>Protect voice rights</h3>
                <p>Give licensed voice assets durable proof of origin across edits, posts, and reuse.</p>
              </div>
              <div className="audience-card">
                <span className="audience-label">Platforms</span>
                <h3>Trace authorized media</h3>
                <p>Support policy, takedown, and verification workflows across large audio libraries.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="features-section">
          <div className="container feature-layout">
            <div className="feature-copy">
              <p className="eyebrow">Core platform</p>
              <h2>Built for ownership, search, and verification.</h2>
              <p>
                The watermarking layer and audio database work together to create a persistent
                identity that can be embedded, indexed, and detected across modern media workflows.
              </p>
            </div>
            <div className="feature-cards">
              <div className="feature-card">
                <span className="feature-index">01</span>
                <h3>Invisible audio watermarking</h3>
                <p>
                  Robust markers sit inside the signal while preserving the natural quality of
                  voice and sound.
                </p>
                <div className="feature-proof">
                  <span>Helps prove origin without changing the listener experience.</span>
                </div>
              </div>
              <div className="feature-card">
                <span className="feature-index">02</span>
                <h3>Advanced audio database</h3>
                <p>
                  Traceable identifiers make search, verification, and provenance workflows
                  practical across large libraries.
                </p>
                <div className="feature-proof">
                  <span>Turns protected audio into a reviewable asset trail.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="audio-comparison-section">
          <div className="container">
            <div className="audio-comparison-card">
              <div>
                <p className="eyebrow">Audio demo</p>
                <h2>Compare the original and watermarked audio.</h2>
              </div>
              <div className="audio-players">
                <AudioWaveformCard
                  title="Original WAV"
                  description="Decoded from the source WAV file and drawn as an actual waveform."
                  src="/audio/AOWL_Introduction - original.wav"
                  type="audio/wav"
                />
                <AudioWaveformCard
                  title="Watermarked MP3"
                  description="The moving playhead tracks playback while ticks show where watermark payloads were inserted."
                  src="/audio/AOWL_Introduction - watermarked.mp3"
                  type="audio/mpeg"
                  variant="watermarked"
                  watermarkMarkers={watermarkedDemoMarkers}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="extension-section">
          <div className="container extension-layout">
            <div className="extension-copy">
              <p className="eyebrow">Chrome extension</p>
              <h2>Try real-time detection in your browser.</h2>
              <p>
                Install the AOWL Active Tab Watermark extension to check active-tab audio for
                AOWL watermark signals while reviewing clips, calls, and published media.
              </p>
              <div className="extension-actions">
                <Button
                  variant="primary"
                  href={chromeExtensionUrl}
                  external={true}
                >
                  Try real-time detection extension
                </Button>
                <span>Available on the Chrome Web Store</span>
              </div>
            </div>

            <a
              className="extension-preview"
              href={chromeExtensionUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Try the AOWL real-time detection Chrome extension"
            >
              <div className="extension-window-top">
                <span className="extension-dot red" />
                <span className="extension-dot yellow" />
                <span className="extension-dot green" />
                <span className="extension-tab-label">active tab audio</span>
              </div>
              <div className="extension-body">
                <div className="extension-title-row">
                  <span className="extension-app-mark">A</span>
                  <div>
                    <strong>AOWL Active Tab Watermark</strong>
                    <span>Real-time detector</span>
                  </div>
                </div>
                <div className="extension-signal">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="extension-result">
                  <span className="extension-pulse" />
                  Watermark signal detected
                </div>
                <div className="extension-meta">
                  <span>Tab listening</span>
                  <span>Review ready</span>
                </div>
              </div>
            </a>
          </div>
        </section>

        <section className="blog-teaser-section">
          <div className="container blog-teaser">
            <div>
              <p className="eyebrow">Technical writing</p>
              <h2>Partner-ready space for tech blogs.</h2>
              <p>
                A clean placeholder page is ready for research notes, engineering explainers,
                and product updates.
              </p>
            </div>
            <Link to="/blog" className="btn btn-secondary">View blog queue</Link>
          </div>
        </section>
      </div>
    );
  }
}

export default HomePage;
